/* eslint-disable no-console */
import _ from 'lodash';
import { batch } from 'react-redux';

import {
  setDeleteEntityOfflineDataDialogOpen,
  setStorageSpaceDialogOpen,
} from './DialogStatesActions';
import { handleErrors } from './ErrorActions';
import { addSuccess } from './SuccessActions';
import { addWarning } from './WarningActions';
import * as activityLogApi from '../api/activityLog';
import * as attachmentsApi from '../api/attachments';
import { getSupportData } from '../api/get-support-data';
import { getSavedSearchResults } from '../api/saved-searches';
import * as taskStatusApi from '../api/task-status';
import { EntitiesDBService } from '../db/entitiesDBService';
import { SearchResultsDBService } from '../db/searchResultsDBService';
import {
  createFullEntity,
  createFullEntityOffline,
  setSupportData,
} from '../form-designer/actions/form-manager.actions';
import {
  FORM_KEYS,
  STATIC_DATA_SOURCE_LIST,
} from '../form-designer/constants/form-manager.constants';
import {
  CRITICAL_STORAGE_CAPACITY,
  EXTERNAL_DATASOURCE_TYPES,
  OFFLINE_TAB_PART_ID,
} from '../form-designer/constants/runtime.constants';
import {
  mapOrderByArrayToSortExpression,
  supportDataAdapter,
} from '../form-designer/utils/actions/data-source.actions.utils';
import { normalizeAttachmentData } from '../form-designer/utils/actions/file-upload.actions.utils';
import {
  getArrayOfPromisesForCreateFullEntity,
  getFormRules,
  preprocessDataSources,
  preprocessFormModel,
} from '../form-designer/utils/actions/form-manager.actions.utils';
import { getSmartSolveDiff } from '../form-designer/utils/diffTracking.utils';
import { getDataPath, preprocessEntityData } from '../form-designer/utils/form-manager.utils';
import { isEmpty, isNilOrEmpty } from '../utils/ramdaUtils';

export const SET_OFFLINE_DATA_ENTITIES = 'SET_OFFLINE_DATA_ENTITIES';
export const START_DOWNLOAD_ENTITY = 'START_DOWNLOAD_ENTITY';
export const DOWNLOAD_ENTITY_FAILURE = 'DOWNLOAD_ENTITY_FAILURE';
export const DOWNLOAD_ENTITY_SUCCESS = 'DOWNLOAD_ENTITY_SUCCESS';
export const SET_UNSYNCHRONIZED_ENTITY_ID = 'SET_UNSYNCHRONIZED_ENTITY_ID';
export const CLEAR_UNSYNCHRONIZED_ENTITY = 'CLEAR_UNSYNCHRONIZED_ENTITY';
export const CLEAR_ENTITY_BY_ID = 'CLEAR_ENTITY_BY_ID';
export const UPDATE_OFFLINE_SEARCH_RESULTS = 'UPDATE_OFFLINE_SEARCH_RESULTS';
export const CLEAR_OFFLINE_SEARCH_RESULTS = 'CLEAR_OFFLINE_SEARCH_RESULTS';

export const setOfflineDataEntities = (entities) => ({
  type: SET_OFFLINE_DATA_ENTITIES,
  payload: entities,
});

export const startDownloadEntity = (id) => ({
  type: START_DOWNLOAD_ENTITY,
  payload: {
    id,
  },
});

export const downloadEntityFailure = (id) => ({
  type: DOWNLOAD_ENTITY_FAILURE,
  payload: {
    id,
  },
});

export const downloadEntitySuccess = (id, entity) => ({
  type: DOWNLOAD_ENTITY_SUCCESS,
  payload: {
    id,
    entity,
  },
});

export const setUnsynchronizedEntityId = (id) => ({
  type: SET_UNSYNCHRONIZED_ENTITY_ID,
  payload: {
    id,
  },
});

export const clearEntityById = (id) => ({
  type: CLEAR_ENTITY_BY_ID,
  payload: {
    id,
  },
});

export const clearUnsynchronizedEntity = () => ({
  type: CLEAR_UNSYNCHRONIZED_ENTITY,
});

export const updateOfflineSearchResults = (searchResult) => ({
  type: UPDATE_OFFLINE_SEARCH_RESULTS,
  payload: {
    searchResult,
  },
});

export const clearOfflineSearchResults = () => ({
  type: CLEAR_OFFLINE_SEARCH_RESULTS,
});

export const clearSearchResultsRow = (entityId) => (dispatch, getState) => {
  const { offlineSearchResults } = getState().offlineData;
  const [itemId, stepId] = entityId.split('_');

  if (isNilOrEmpty(offlineSearchResults)) {
    return;
  }

  const { id, rows } = offlineSearchResults;

  const rowIndex = offlineSearchResults.rows.findIndex(
    (ele) => ele?.ItmId === itemId && ele?.StepId === stepId
  );

  let newRows = [];

  if (rowIndex !== -1) {
    newRows = [...rows];
    newRows.splice(rowIndex, 1);
  } else {
    newRows = [...rows];
  }

  const isNewRowsEmpty = isEmpty(newRows);

  const searchResultDBKey = `${id} + ${localStorage.getItem('userCacheKey')}`;

  const newSearchResult = { ...offlineSearchResults, rows: newRows };

  if (isNewRowsEmpty) {
    SearchResultsDBService.deleteSearchResult(searchResultDBKey);
    dispatch(clearOfflineSearchResults());
  } else {
    SearchResultsDBService.saveSearchResult({ ...newSearchResult, id: searchResultDBKey });
    dispatch(updateOfflineSearchResults(newSearchResult));
  }
};

export const checkStorageSpaceAvailable = async () => {
  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage;

    if (_.isNil(usage)) {
      return true;
    }

    return CRITICAL_STORAGE_CAPACITY >= usage;
  } catch (error) {
    console.log('checkStorageCapacity() error: ', error);
    return true;
  }
};

export const handleSupportData = async (dataSources) => {
  if (Object.values(dataSources).length === 0) {
    return Promise.resolve([]);
  }

  return Promise.all(
    Object.values(dataSources)
      .filter((request) => EXTERNAL_DATASOURCE_TYPES.includes(request.dataSourceTypeStr))
      .map((request) => {
        const requestData = _.omit(request, ['containerFormName']);
        const orderByArr = requestData.orderBy;
        requestData.filter = [];

        if (orderByArr && orderByArr.length > 0) {
          requestData.sortExpression = mapOrderByArrayToSortExpression(orderByArr);
        }

        if (request.dataSourceTypeStr === 'LISTDATA') {
          requestData.columnList = STATIC_DATA_SOURCE_LIST;
        }

        return getSupportData(requestData).then((data) => {
          if (_.isNil(data.GetSupportData)) {
            return { cachedSupportData: {}, supportData: {} };
          }

          const cachedSupportData = {
            [requestData?.dataSourceName ?? '']: data,
          };

          const dataRows = data.GetSupportData.rows;

          if (isNilOrEmpty(dataRows)) {
            return { cachedSupportData: {}, supportData: {} };
          }

          const parsedSupportData = supportDataAdapter(dataRows, [
            ...requestData.columnList,
            'LNG_TRANSLATION',
          ]);

          const supportData = {
            [requestData?.dataSourceName ?? '']: parsedSupportData,
          };

          return { cachedSupportData, supportData };
        });
      })
  );
};

export const getSavedSearchResultsByTaskID = async (searchParams, data) => {
  const taskId = data.entityDataStore.entityData?.CurrentPhaseStepData?.TaskId;

  if (!taskId) {
    return;
  }

  const searchCriteria = {
    AllowEditing: 'true',
    CloseGroup: ' ',
    ColumnType: 'System.String',
    Condition: 'EQ',
    EnableLookup: 'true',
    FieldName: 'V_ALL_WORKFLOW_TASK.TSK_ID',
    OpenGroup: ' ',
    Operator: 'and',
    Value: taskId,
  };

  const newSearchParams = {
    ...searchParams,
    filter: [],
    advancedsearchcriteria: [...(searchParams.advancedsearchcriteria ?? []), searchCriteria],
  };

  delete newSearchParams.dataKey;

  try {
    const fetchedSearchResults = await getSavedSearchResults(newSearchParams);

    if (fetchedSearchResults.hasError) {
      return;
    }

    return fetchedSearchResults.SearchData;
  } catch (error) {
    console.log('getSavedSearchResultsByTaskID(): catch: ', error);
    return undefined;
  }
};

export const createSearchResultDBRecord = (savedSearchResult, existingRows, itmId, stepId) => {
  return Object.entries(savedSearchResult).reduce((acc, [key, item]) => {
    const results = item.results;
    let newRows = [];

    if (isNilOrEmpty(results)) {
      return acc;
    }

    const row = results.rows.find((ele) => ele?.ItmId === itmId && ele?.StepId === stepId);

    if (!isNilOrEmpty(row)) {
      newRows = [
        ...existingRows.filter((ele) => !(ele?.ItmId === itmId && ele?.StepId === stepId)),
        row,
      ];
    } else {
      return acc;
    }

    return {
      ...acc,
      ...results,
      id: key,
      rows: newRows,
      totalResultCount: newRows.length,
    };
  }, {});
};

export const downloadSavedSearchResults = ({ itmId, stepId, data }) => async (
  dispatch,
  getState
) => {
  const { offlineData, savedSearchResults = {} } = getState();
  const offlineSearchResults = offlineData.offlineSearchResults;
  const savedSearchResult = _.pickBy(savedSearchResults, (value, key) =>
    key.includes(OFFLINE_TAB_PART_ID)
  );

  if (isNilOrEmpty(savedSearchResult)) {
    return;
  }

  const searchResultKey = Object.keys(savedSearchResult)[0];

  let newSearchResult = createSearchResultDBRecord(
    savedSearchResult,
    offlineSearchResults.rows ?? [],
    itmId,
    stepId
  );

  if (isNilOrEmpty(newSearchResult)) {
    const searchParams = savedSearchResult[searchResultKey].searchParams;
    const fetchedSearchResult = await getSavedSearchResultsByTaskID(searchParams, data);

    if (!fetchedSearchResult) {
      return;
    }

    newSearchResult = createSearchResultDBRecord(
      { [searchResultKey]: { results: fetchedSearchResult } },
      offlineSearchResults.rows ?? [],
      itmId,
      stepId
    );
  }

  dispatch(updateOfflineSearchResults(newSearchResult));

  const searchResultDBKey = `${searchResultKey} + ${localStorage.getItem('userCacheKey')}`;

  SearchResultsDBService.saveSearchResult({ ...newSearchResult, id: searchResultDBKey });
};

export const downloadEntity = ({
  entityViewName,
  entityType,
  orgId,
  formKey,
  containerWorkflowKey,
  workflowKey,
  workflowId,
  stepId,
  itmId,
  entityName,
  parentWorkflowId,
}) => async (dispatch, getState) => {
  const entityId = `${itmId}_${stepId}`;
  const { formDesigner } = getState();

  try {
    const apiData = await taskStatusApi.getTaskStatus(itmId, entityType);

    if (apiData.hasError) {
      dispatch(handleErrors(apiData.errors));
    }

    const [
      stepFormResponse,
      detailFormResponse,
      entityResponse,
      formDataResponse,
      stepFormTranslationsResponse,
      detailFormTranslationsResponse,
    ] = await Promise.all(
      getArrayOfPromisesForCreateFullEntity({
        orgId,
        containerWorkflowKey,
        workflowKey,
        workflowId,
        stepId,
        formKey,
        entityType,
        itmId,
        parentWorkflowId,
        entityName,
      })
    );

    if (
      stepFormResponse.hasError ||
      detailFormResponse.hasError ||
      entityResponse.hasError ||
      formDataResponse.hasError
    ) {
      batch(() => {
        dispatch(downloadEntityFailure(entityId));
        stepFormResponse.hasError && dispatch(handleErrors(stepFormResponse.errors));
        detailFormResponse.hasError && dispatch(handleErrors(detailFormResponse.errors));
        entityResponse.hasError && dispatch(handleErrors(entityResponse.errors));
        formDataResponse.hasError && dispatch(handleErrors(formDataResponse.errors));
      });
      return;
    }

    const stepFormDefinition = stepFormResponse.formDefinition;
    const { dataSources } = stepFormDefinition;
    const preprocessStepDataSources = preprocessDataSources(dataSources);
    const supportDataItems = await handleSupportData(preprocessStepDataSources);

    const cachedSupportData = _.reduce(
      supportDataItems.map((ele) => ele?.cachedSupportData ?? {}),
      function(memo, current) {
        return _.assign(memo, current);
      },
      {}
    );
    const supportData = _.reduce(
      supportDataItems.map((ele) => ele?.supportData ?? {}),
      function(memo, current) {
        return _.assign(memo, current);
      },
      {}
    );

    if (!isNilOrEmpty(formDesigner[FORM_KEYS.STEP])) {
      dispatch(setSupportData(FORM_KEYS.STEP, cachedSupportData, supportData));
    }

    const detailFormDefinition = detailFormResponse.formDefinition;
    const { entityDefinition } = entityResponse;
    const workflowInfo = {
      entityViewName,
      entityType,
      orgId,
      formKey,
      containerWorkflowKey,
      workflowKey,
      workflowId,
      stepId,
      itmId,
      parentWorkflowId: parentWorkflowId ?? '',
    };

    const _entityName = Object.keys(entityDefinition)[0];
    const data = preprocessEntityData(formDataResponse, entityDefinition, _entityName);
    const originalEntityDataStore = data.originalEntityDataStore;
    delete data.originalEntityDataStore;
    // creating original data if empty
    let originalData;

    if (data.entityDataStore.entityData) {
      originalData = originalEntityDataStore
        ? { ...data, entityDataStore: originalEntityDataStore }
        : data;
    } else {
      originalData = {
        ...data,
        entityDataStore: {
          entityData: {
            [_entityName]: {},
          },
        },
      };
    }

    const preprocessedStepFormModel = preprocessFormModel(stepFormDefinition.formModel);
    const preprocessedDetailFormModel = preprocessFormModel(detailFormDefinition.formModel);

    const stepLoadObjs = {
      formModel: preprocessedStepFormModel,
      entityDefinition,
      dataSources: preprocessStepDataSources,
      formInfo: { ...stepFormDefinition.formInfo },
      workflowInfo,
      formRules: getFormRules(preprocessedStepFormModel),
      translations: stepFormTranslationsResponse.Translations,
      entityResolverRules: stepFormTranslationsResponse.EntityResolverRules,
      data,
      originalData,
      cachedSupportData,
      supportData,
      taskStatus: apiData.taskStatus,
    };

    const detailLoadObjs = {
      formModel: preprocessedDetailFormModel,
      entityDefinition,
      dataSources: preprocessDataSources(detailFormDefinition.dataSources),
      formInfo: { ...detailFormDefinition.formInfo },
      workflowInfo,
      formRules: getFormRules(preprocessedDetailFormModel),
      translations: detailFormTranslationsResponse.Translations,
      data,
    };

    const entity = {
      step: stepLoadObjs,
      detail: detailLoadObjs,
    };

    const entityDBId = `${entityId} + ${localStorage.getItem('userCacheKey')}`;
    await EntitiesDBService.saveEntity({ id: entityDBId, entity });

    dispatch(downloadEntitySuccess(entityId, entity));
    dispatch(downloadSavedSearchResults({ itmId, stepId, data }));
    dispatch(addSuccess({ message: 'Task Saved Successfully' }));

    activityLogApi.saveOfflineLog({
      entityName: entityType,
      currentWorkflowId: workflowId,
      stepId,
      workflowKey,
      itmId,
    });
  } catch (error) {
    console.log('downloadEntity(): catch: ', error);
    dispatch(downloadEntityFailure(entityId));
  }
};

export async function uploadUnsyncAttachment(file, fileData, entityData) {
  try {
    const apiData = await attachmentsApi.uploadAttachment(file);

    if (apiData.hasError) {
      throw apiData.errors;
    }

    const { data } = entityData.entity.step;
    const { formInfo } = data;
    const rowId = fileData.rowId;
    const PARENT_PK_ID = fileData.PARENT_PK_ID;

    const result = normalizeAttachmentData({
      uploadData: apiData.Result,
      PARENT_PK_ID,
      formInfo,
      rowId,
      parentData: fileData.parentData,
    });

    return Promise.resolve(result);
  } catch (error) {
    return Promise.reject(error);
  }
}

export const synchronizeEntityFilesChanges = async (savedEntityData) => {
  const files = savedEntityData.files ?? [];
  const index = [0];
  const { entityDefinition, data, originalData } = savedEntityData.entity.step;
  const entityType = Object.keys(entityDefinition)[0];

  try {
    const fileResponses = files.map((fileData) => {
      const file = new File([fileData.buffer], fileData.fileName, { type: fileData.fileType });

      return uploadUnsyncAttachment(file, fileData, savedEntityData).then((attachmentData) => {
        const path = getDataPath(fileData.entityName, index, entityDefinition);
        const parentPath = path.slice(0, -1);

        const allRows = _.get(
          savedEntityData.entity.step.data.entityDataStore?.entityData,
          parentPath,
          []
        );

        const filteredRows = allRows.filter((row) => row.BAH_ID !== fileData.rowId);

        const foundRow = allRows.find((row) => row.BAH_ID === fileData.rowId);
        const BAH_DESCRIPTION = foundRow?.BAH_DESCRIPTION;

        const rows = [...filteredRows, { ...attachmentData, BAH_DESCRIPTION }];

        const updatedEntityData =
          _.cloneDeep(savedEntityData.entity.step.data.entityDataStore?.entityData) || {};
        _.set(updatedEntityData, parentPath, rows);

        savedEntityData.entity.step.data.entityDataStore = {
          ...savedEntityData.entity.step.data.entityDataStore,
          entityData: updatedEntityData,
        };
      });
    });

    await Promise.all(fileResponses);

    const newDiff = getSmartSolveDiff(
      originalData.entityDataStore,
      data.entityDataStore,
      entityType,
      entityDefinition
    );

    savedEntityData.requestChanges.clientChanges = newDiff;

    return savedEntityData.requestChanges;
  } catch (error) {
    console.log('synchronizeEntityFilesChanges() error: ', error);
    return savedEntityData.requestChanges;
  }
};

export const synchronizeEntityData = async (entityType, savedEntityData) => {
  const hasFiles = !isNilOrEmpty(savedEntityData.files);
  let changes = { ...savedEntityData.requestChanges };

  if (hasFiles) {
    changes = await synchronizeEntityFilesChanges(savedEntityData);
  }

  return await activityLogApi.synchronizeOfflineLog({
    entityName: entityType,
    submitworkflowdata: changes,
  });
};

export const updateFullEntity = (entityId, stepEntity, detailEntity) => async (
  dispatch,
  getState
) => {
  const { start, tasks, disabled } = getState().taskStatusPanel;

  try {
    const entity = {
      step: { ...stepEntity, taskStatus: { start, tasks, disabled } },
      detail: { ...detailEntity },
    };

    const entityDBId = `${entityId} + ${localStorage.getItem('userCacheKey')}`;
    await EntitiesDBService.updateEntity({ id: entityDBId, entity });
  } catch (error) {
    console.log('updateFullEntity(): catch: ', error);
  }
};

export function fetchOfflineEntities() {
  return async (dispatch) => {
    try {
      const result = await EntitiesDBService.fetchAllEntities();
      dispatch(setOfflineDataEntities(result));
      return result;
    } catch (error) {
      console.log('fetchOfflineEntities() catch: ', error);
      dispatch(setOfflineDataEntities([]));
    }
  };
}

export function fetchOfflineSearchResults() {
  return async (dispatch) => {
    try {
      const result = await SearchResultsDBService.fetchAllSearchResults();
      result.forEach((item) => {
        dispatch(updateOfflineSearchResults(item));
      });
      return result;
    } catch (error) {
      console.log('fetchOfflineSearchResults() catch: ', error);
      dispatch(setOfflineDataEntities([]));
    }
  };
}

export function downloadFullEntity(entityRow) {
  return async (dispatch) => {
    const { ItmId, StepId, EntityType, FormKey, WorkFlowId } = entityRow;
    const entityId = `${ItmId}_${StepId}`;

    const storageSpaceAvailable = await checkStorageSpaceAvailable();

    if (!storageSpaceAvailable) {
      dispatch(setStorageSpaceDialogOpen(true));
      return;
    }

    batch(() => {
      dispatch(startDownloadEntity(entityId));
      dispatch(
        downloadEntity({
          entityViewName: '',
          entityName: '',
          entityType: EntityType,
          stepId: StepId,
          itmId: ItmId,
          formKey: FormKey,
          workflowId: WorkFlowId,
          workflowKey: '',
          containerWorkflowKey: '',
          parentWorkflowId: '',
        })
      );
    });
  };
}

export function deleteEntityFromLocalDB(entityId, hasUnsynchedChanges = false) {
  return async (dispatch, getState) => {
    const { entities } = getState().offlineData;
    const savedEntityData = _.find(entities, { id: entityId });
    const {
      entity: {
        step: {
          workflowInfo: { workflowId, stepId, itmId, entityType, workflowKey },
        },
      },
    } = savedEntityData || { entity: { step: { workflowInfo: {} } } };

    dispatch(clearEntityById(entityId));
    dispatch(clearSearchResultsRow(entityId));
    dispatch(clearUnsynchronizedEntity());
    const entityDBId = `${entityId} + ${localStorage.getItem('userCacheKey')}`;
    await EntitiesDBService.removeEntity(entityDBId);

    if (!hasUnsynchedChanges) {
      return;
    }

    activityLogApi.discardOfflineLog({
      entityName: entityType,
      currentWorkflowId: workflowId,
      stepId,
      workflowKey,
      itmId,
    });
  };
}

export function deleteEntityFromLocalDBIfNeeded(entityRow) {
  return (dispatch, getState) => {
    const { ItmId, StepId } = entityRow;
    const state = getState();
    const entityId = `${ItmId}_${StepId}`;
    const entities = _.get(state, ['offlineData', 'entities'], []);
    const savedEntityData = entities.find((entity) => entity.id === entityId);
    const clientChanges = savedEntityData?.requestChanges;
    const hasUnsynchedChanges = !isNilOrEmpty(clientChanges);

    if (hasUnsynchedChanges) {
      dispatch(setDeleteEntityOfflineDataDialogOpen(true, entityId));
      return;
    }

    dispatch(deleteEntityFromLocalDB(entityId, false));
    dispatch(addWarning({ message: 'Local Copy Deleted Successfully' }));
  };
}

export function removeUnsynchronizedEntityAndRefetch() {
  return async (dispatch, getState) => {
    const { entities, unsynchronizedEntityId } = getState().offlineData;
    const savedEntityData = entities.find((entity) => entity.id === unsynchronizedEntityId);
    const {
      entity: {
        step: {
          workflowInfo: {
            workflowId,
            stepId,
            itmId,
            entityType,
            formKey,
            workflowKey,
            containerWorkflowKey,
            parentWorkflowId,
          },
        },
      },
    } = savedEntityData || { entity: { step: { workflowInfo: {} } } };

    try {
      await dispatch(deleteEntityFromLocalDB(unsynchronizedEntityId, true));

      dispatch(
        createFullEntity({
          entityViewName: '',
          entityType,
          orgId: '',
          formKey,
          containerWorkflowKey,
          workflowKey,
          workflowId,
          stepId,
          itmId,
          entityName: '',
          parentWorkflowId,
        })
      );
    } catch (error) {
      console.log('removeUnsynchronizedEntityAndRefetch() catch: ', error);
    }
  };
}

export function synchronizeEntity() {
  return async (dispatch, getState) => {
    const state = getState();
    const { unsynchronizedEntityId, entities } = state.offlineData;
    const savedEntityData = entities.find((entity) => entity.id === unsynchronizedEntityId);
    const {
      entity: {
        step: {
          workflowInfo: {
            workflowId,
            stepId,
            itmId,
            entityType,
            formKey,
            workflowKey,
            containerWorkflowKey,
            parentWorkflowId,
          },
        },
      },
    } = savedEntityData || { entity: { step: { workflowInfo: {} } } };
    const entityId = `${itmId}_${stepId}`;

    try {
      const response = await synchronizeEntityData(entityType, savedEntityData);
      const { SubmitWorkflowResult } = response.submitWorkflowData;

      if (SubmitWorkflowResult && SubmitWorkflowResult.hasError) {
        const { errors = [] } = SubmitWorkflowResult ?? {};
        dispatch(createFullEntityOffline(savedEntityData));
        dispatch(handleErrors(errors, { noFilter: true }));
        console.log(`submitWorkflowData() has error, data : ${JSON.stringify(response)}`);
        return;
      }

      await dispatch(
        createFullEntity({
          entityViewName: '',
          entityType,
          orgId: '',
          formKey,
          containerWorkflowKey,
          workflowKey,
          workflowId,
          stepId,
          itmId,
          entityName: '',
          parentWorkflowId,
        })
      );

      if (SubmitWorkflowResult && !SubmitWorkflowResult.hasError) {
        dispatch(deleteEntityFromLocalDB(entityId, false));
      }
    } catch (error) {
      console.log('synchronizeEntity catch: error: ', error);
    }
  };
}
